import {Locale, useLocale, useMessages} from "i18n";
import {pickerLocales} from "./languageChoices";

/** One selectable locale row in the standalone globe language picker. */
export interface LanguageChoice {
	/** Locale this row switches to. */
	code: Locale;
	/** Endonym label (the language's own name — identical in every catalog). */
	label: string;
	/** Two-letter code shown on the compact globe trigger (EN / RO). */
	short: string;
	/** Flag emoji for the picker row — locale-intrinsic (same in every catalog). */
	flag: string;
	/** Whether this is the currently-active locale. */
	active: boolean;
}

/**
 * Flag emoji per locale. Like the endonyms and the 2-letter code, these are locale-INTRINSIC (not
 * translated), so they live once here rather than in the message catalogs (ui#111).
 */
const LOCALE_FLAG: Record<Locale, string> = {en: "🇬🇧", ro: "🇷🇴", de: "🇩🇪", fr: "🇫🇷", it: "🇮🇹"};

/**
 * Shared source for the standalone globe language picker (ui#111 — the owner-chosen replacement for
 * the in-menu rows of ui#109). Returns the shipped choices in order (endonym + 2-letter code + flag +
 * which is active), the currently-active `current` choice (to render the compact trigger), the
 * heading/accessible name, and the persisting setter — so every language surface switches locale
 * through the SAME {@link useLocale} logic (ui#101) instead of forking it. Selecting a choice persists
 * to localStorage `locale-v1` and re-renders the app; there is no URL or domain change.
 */
export function useLanguageChoices(): {
	choices: LanguageChoice[];
	/** The currently-active choice — feeds the compact globe trigger (🌐 EN / 🌐 RO). */
	current: LanguageChoice;
	setLocale: (locale: Locale) => void;
	/** Heading / accessible name for the picker ("Language" / "Limbă"). */
	label: string;
} {
	const {locale, canonicalLocale, setLocale} = useLocale();
	const {nav} = useMessages();
	// Rows in the one order the app declares the shipped locales (`SITE_LOCALES`, the brand registry's list), so a
	// new market appears in the picker by launching rather than by being remembered here as well, minus the
	// languages that belong to another brand than the page's.
	const choices: LanguageChoice[] = pickerLocales(canonicalLocale).map((code) => ({
		code,
		label: nav.languages[code],
		short: code.toUpperCase(),
		flag: LOCALE_FLAG[code],
		active: locale === code,
	}));
	const current = choices.find((choice) => choice.active) ?? choices[0];
	return {choices, current, setLocale, label: nav.language};
}
